import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, BookMarked, ArrowLeft, FileText } from 'lucide-react';
import { publications, categoryConfig } from '../../data/publications';

const PublicationSkeleton = () => (
  <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden animate-pulse">
    <div className="w-full h-48 bg-slate-200" />
    <div className="p-6">
      <div className="flex items-center space-x-4 mb-3">
        <div className="h-4 w-24 bg-slate-200 rounded" />
        <div className="h-4 w-32 bg-slate-200 rounded" />
      </div>
      <div className="h-6 bg-slate-200 rounded mb-3 w-3/4" />
      <div className="space-y-2">
        <div className="h-4 bg-slate-200 rounded w-full" />
        <div className="h-4 bg-slate-200 rounded w-5/6" />
      </div>
    </div>
  </div>
);

export default function CategoryPublications() {
  const { category } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const categoryPublications = category === 'all'
    ? publications
    : publications.filter(pub => pub.category === category);

  useEffect(() => {
    window.scrollTo(0, 0);
    setIsLoading(true);
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [category]);

  if (!category || (category !== 'all' && !categoryConfig[category as keyof typeof categoryConfig])) {
    return (
      <div className="min-h-screen bg-white pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h1 className="text-4xl font-bold text-slate-900">Category not found</h1>
          <Link to="/collections" className="text-emerald-600 hover:text-emerald-700 mt-4 inline-block">
            Back to Collections
          </Link>
        </div>
      </div>
    );
  }

  const categoryInfo = category === 'all'
    ? { title: 'All Publications', description: 'Browse our complete collection of publications' }
    : categoryConfig[category as keyof typeof categoryConfig];

  return (
    <div className="min-h-screen bg-white pt-20">
      <div className="bg-slate-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/collections"
            className="inline-flex items-center text-emerald-400 hover:text-emerald-300 mb-6 transition"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Collections
          </Link>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            {categoryInfo.title}
          </h1>
          <p className="text-xl text-gray-300 mb-4">
            {categoryInfo.description}
          </p>
          <div className="flex items-center text-gray-400">
            <FileText className="h-5 w-5 mr-2" />
            <span className="text-lg">
              {categoryPublications.length} {categoryPublications.length === 1 ? 'publication' : 'publications'} in this collection
            </span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {Array.from({ length: 6 }).map((_, index) => (
              <PublicationSkeleton key={index} />
            ))}
          </div>
        ) : categoryPublications.length === 0 ? (
          <div className="text-center py-16">
            <FileText className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <p className="text-xl text-slate-500">No publications found in this category</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {categoryPublications.map((pub) => (
              <Link
                key={pub.id}
                to={`/publications/${pub.id}`}
                className="group bg-white rounded-xl shadow-sm hover:shadow-xl transition border border-slate-200 overflow-hidden"
              >
                <div className="aspect-w-16 aspect-h-9">
                  <img
                    src={pub.coverImage}
                    alt={pub.title}
                    className="w-full h-48 object-cover transform group-hover:scale-105 transition duration-300"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center space-x-4 text-sm text-slate-500 mb-3">
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-1" />
                      <span>{pub.date}</span>
                    </div>
                    <div className="flex items-center">
                      <BookMarked className="h-4 w-4 mr-1" />
                      <span className="capitalize">
                        {categoryConfig[pub.category as keyof typeof categoryConfig]?.title || pub.category}
                      </span>
                    </div>
                  </div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-3 group-hover:text-emerald-600 transition line-clamp-2">
                    {pub.title}
                  </h2>
                  <p className="text-slate-600 text-sm line-clamp-3">
                    {pub.abstract}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}